import { format } from '@screentone/addon-calendar';
import validateDate from './utils/validateDate';

import { PLOT_BAND_CLASS_NAME } from '../plot-bands.classNames';
import { LANGUAGES, PROPERTIES, RECESSION_LABEL_TEXT } from '../plot-bands.constants';
import { getAnnotationPositionValue } from '../plot-bands.utils';
import { CATEGORY_FORMAT, LINEAR_SERIES_MAX_LENGTH } from './plotBandPresets.constants';

/**
 * Given an array of `recessionData` filter out data that pertains to dates outside of the minimum
 * and maximum dates in the chart.
 *
 * @param {RecessionData[]} recessionData Recession data for all recessions
 * @param {number} minDate The earliest date in the chart
 * @param {number} maxDate The latest date in the chart
 * @return {RecessionData[]} Recession data that is relevant within the given chart
 */
const filterRecessionDataByDate = (recessionData, minDate, maxDate) =>
  recessionData.filter((recession) => recession.from >= minDate && recession.to <= maxDate);

/**
 * Given data about the current chart, return a flag that indicates whether or not the chart's data
 * is linear.
 *
 * @param {boolean} quarterlyResults A flag that indicates whether or not quarterly results are
 * being displayed in the chart
 * @param series Options for the chart's data
 * @returns {boolean} A flag that indicates whether or not the current chart is linear
 */
export const getIsLinear = (quarterlyResults, series) =>
  quarterlyResults && series[0].data.length <= LINEAR_SERIES_MAX_LENGTH;

/**
 * For quarterly results, the x-axis is linear and just pretends to be a date. We need to convert
 * the dates we have for the plot bands into a value that represents an index on the x-axis.
 *
 * @param {number} index The index of the date in `categories` that represents the start or end of a plot band
 * @param {number} dateNumber A number that represents a date of the start or end of a plot band
 * @param {string[]} categories An array of strings that represent dates that the chart displays data for
 * @param {boolean} reverseDateMonth A boolean indicating whether the date should be reversed in the chart
 * @return {number} A number that represents the start or end of a plot band on a linear x-axis
 */
export const getLinearPrecise = (index, dateNumber, categories, reverseDateMonth) => {
  let linear = index;

  if (index < categories.length - 1) {
    const startOrEndOfPlotBand = validateDate(categories[index], reverseDateMonth).getTime();
    const dateAfterStartOrEndOfPlotBand = validateDate(
      categories[index + 1],
      reverseDateMonth
    ).getTime();

    // difference between the start of the recession and the closest x-axis interval
    const startAndClosestDiff = dateNumber - startOrEndOfPlotBand;

    // difference between the interval after start of the recession on the x-axis and the interval
    // where the recession started
    const endAndClosestDiff = dateAfterStartOrEndOfPlotBand - startOrEndOfPlotBand;

    linear += startAndClosestDiff / endAndClosestDiff;
  }

  return linear;
};

/**
 * Get recession text based on publication and CSS object
 *
 * @param {Property} currentProperty The publication of the current chart
 * @param {ChartFont} chartFont A string that represents the language used in the current chart
 * @returns {string} An object containing the annotation text of the plot bands and the css of the plot bands
 */
export const getRecessionText = (currentProperty, chartFont) => {
  let annotation = RECESSION_LABEL_TEXT.EN;

  // annotation text
  if (currentProperty === PROPERTIES.BARRONS) annotation = RECESSION_LABEL_TEXT.BARRONS;
  else if (chartFont === LANGUAGES.CHINESE) annotation = RECESSION_LABEL_TEXT.CH;
  else if (chartFont === LANGUAGES.JAPANESE) annotation = RECESSION_LABEL_TEXT.JA;

  return annotation;
};

/**
 * Given `recessionData` and a list of dates that the chart's data covers, `categories`, filter out
 * recession data that is not relevant to the current chart.
 *
 * @param {PlotBandPresetState} state An object containing state related to plotBandPresets
 * @param {{ id: number | string, recessionData: RecessionData[] }} options An object containing
 * recession data
 * @returns {PlotBand[]} An array of filtered recession data
 */
export const getPlotBandPresetsWithinRange = (
  { categories, chartFont, currentProperty, isVideo, reverseDateMonth },
  { id, recessionData }
) => {
  const minDateInChart = validateDate(categories[0], reverseDateMonth).getTime();
  const maxDateInChart = validateDate(
    categories[categories.length - 1],
    reverseDateMonth
  ).getTime();

  const annotation = getRecessionText(currentProperty, chartFont);
  return filterRecessionDataByDate(recessionData, minDateInChart, maxDateInChart).map(
    (recession, i) => ({
      id,
      className: PLOT_BAND_CLASS_NAME.COLORS.RECESSION,
      label: {
        align: 'left',
        className: PLOT_BAND_CLASS_NAME.ANNOTATION,
        text: !i ? annotation : '',
        y: getAnnotationPositionValue({ isVideo, isTop: true, currentProperty }),
      },
      from: Math.max(minDateInChart, recession.from),
      to: Math.min(maxDateInChart, recession.to),
    })
  );
};

/**
 * A function for formatting recession data for linear charts.
 *
 * @param {PlotBandPresetState} state An object containing state related to plotBandPresets
 * @param {{ id: number | string, recessionData: RecessionData[] }} options An object containing
 * recession data
 * @returns {PlotBand[]} An array of filtered recession data
 */
export const getLinearPlotBandPresetsWithinRange = (
  { categories, chartFont, currentProperty, isVideo, reverseDateMonth },
  { id, recessionData }
) => {
  const minDateInChart = validateDate(categories[0], reverseDateMonth).getTime();
  const maxDateInChart = validateDate(
    categories[categories.length - 1],
    reverseDateMonth
  ).getTime();

  return filterRecessionDataByDate(recessionData, minDateInChart, maxDateInChart).map((band, i) => {
    const fromString = format(band.from, CATEGORY_FORMAT);
    const toString = format(band.to, CATEGORY_FORMAT);

    let fromIndex = null;
    let toIndex = null;

    /* eslint-disable-next-line no-plusplus */
    for (let j = 0, k = categories.length - 1; j < k; j++, k--) {
      if (fromString === categories[j]) fromIndex = j;
      if (toString === categories[k]) toIndex = k;
      if (fromIndex && toIndex) break;
    }

    const annotation = getRecessionText(currentProperty, chartFont);

    return {
      id,
      className: PLOT_BAND_CLASS_NAME.COLORS.RECESSION,
      label: {
        align: 'left',
        className: PLOT_BAND_CLASS_NAME.ANNOTATION,
        text: !i ? annotation : '',
        y: getAnnotationPositionValue({ isVideo, isTop: true, currentProperty }),
      },
      from: getLinearPrecise(fromIndex, band.from, categories, reverseDateMonth),
      to: getLinearPrecise(toIndex, band.from, categories, reverseDateMonth),
    };
  });
};
